// maths.portll.net — Firebase client contract.
//
// One init, one auth funnel (ported from tidal-trigger app/game/firebase.js): every
// sign-in provider flows through a single onAuthStateChanged listener that the
// AuthContext subscribes to. Emulator wiring is env-gated (VITE_USE_EMULATORS=1) and
// points at the ports in the repo firebase.json. The web API key is not a secret —
// security is enforced by Firestore/Storage rules + App Check, not by hiding config.

import { initializeApp } from "firebase/app";
import {
  getAuth,
  connectAuthEmulator,
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  linkWithPopup,
} from "firebase/auth";
import {
  getFirestore,
  connectFirestoreEmulator,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator, httpsCallable } from "firebase/functions";

const USE_EMULATORS =
  import.meta.env.VITE_USE_EMULATORS === "1" || import.meta.env.VITE_USE_EMULATORS === "true";

// getAuth(app) throws `auth/invalid-api-key` at MODULE LOAD if apiKey is missing — which
// would white-screen the entire app just because the Firebase project isn't wired yet. So we
// (a) give the emulator a dummy key (the auth emulator ignores it), and (b) when there is no
// real key AND no emulator, run UNCONFIGURED: `auth` is null and the app degrades gracefully
// (public pages render; auth-gated routes show a "backend not set up yet" state) instead of
// crashing. Fill VITE_FIREBASE_* from the real `maths-games` project to make auth functional.
const apiKey = import.meta.env.VITE_FIREBASE_API_KEY || (USE_EMULATORS ? "demo-api-key" : undefined);

const firebaseConfig = {
  apiKey,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "demo-maths-games",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

/** True when a Firebase auth apiKey is present (real project or emulator). */
export const isConfigured = Boolean(apiKey);

export const app = initializeApp(firebaseConfig);
export const auth = isConfigured ? getAuth(app) : null; // null ⇒ unconfigured, app still renders
export const db = getFirestore(app);
export const storage = getStorage(app);
// Functions region MUST match functions/index.js setGlobalOptions region.
export const functions = getFunctions(app, "australia-southeast1");

// ── emulator wiring (local dev/test only) ────────────────────────────────────
if (USE_EMULATORS && auth && !globalThis.__MATHS_EMULATORS_WIRED__) {
  globalThis.__MATHS_EMULATORS_WIRED__ = true; // guard against Vite HMR double-wiring
  connectAuthEmulator(auth, "http://127.0.0.1:9098", { disableWarnings: true });
  connectFirestoreEmulator(db, "127.0.0.1", 8085);
  connectStorageEmulator(storage, "127.0.0.1", 9198);
  connectFunctionsEmulator(functions, "127.0.0.1", 5002);
}

// ── the single auth funnel ───────────────────────────────────────────────────
/** Subscribe to auth changes; returns the unsubscribe fn. */
export const onAuthChange = (cb) => {
  if (!auth) { cb(null); return () => {}; } // unconfigured: report "signed out", never crash
  return onAuthStateChanged(auth, cb);
};

const googleProvider = new GoogleAuthProvider();
export const signInWithGoogle = () => signInWithPopup(auth, googleProvider);

const appleProvider = new OAuthProvider("apple.com");
appleProvider.addScope("email");
appleProvider.addScope("name");
export const signInWithApple = () => signInWithPopup(auth, appleProvider);
// Link Apple to an existing (e.g. Google) account — Apple private-relay emails will
// NOT auto-match, so this must exist before the iPad app ships.
export const linkApple = () => (auth.currentUser ? linkWithPopup(auth.currentUser, appleProvider) : Promise.reject(new Error("not signed in")));

export const signInWithEmail = (email, password) => signInWithEmailAndPassword(auth, email, password);
export const registerWithEmail = (email, password) => createUserWithEmailAndPassword(auth, email, password);
export const resetPassword = (email) => sendPasswordResetEmail(auth, email);
export const signOutUser = () => signOut(auth);

// ── parent + access docs ─────────────────────────────────────────────────────
/** Ensure the parent profile doc exists (client-owned, NON-trusted fields only). */
export async function ensureParentDoc(user) {
  const ref = doc(db, `users/${user.uid}`);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      email: user.email || "",
      displayName: user.displayName || "",
      createdAt: serverTimestamp(),
    });
  }
  return ref;
}

/** Read the trusted access plane (betaAccess). Client can read, never write. */
export async function getAccess(uid) {
  const snap = await getDoc(doc(db, `users/${uid}/private/access`));
  return snap.exists() ? snap.data() : { betaAccess: false };
}

// ── callable wrappers (the trusted-plane writers) ────────────────────────────
export const callSetChildPin = httpsCallable(functions, "setChildPin");
export const callClearChildPin = httpsCallable(functions, "clearChildPin");
export const callVerifyChildPin = httpsCallable(functions, "verifyChildPin");
export const callSetParentPin = httpsCallable(functions, "setParentPin");
export const callVerifyParentPin = httpsCallable(functions, "verifyParentPin");
export const callRedeemInvite = httpsCallable(functions, "redeemInvite");
export const callDeleteChildProfile = httpsCallable(functions, "deleteChildProfile");
export const callDeleteAccount = httpsCallable(functions, "deleteAccount");
